import React, { Fragment, useState } from 'react';
import {
  CurrentStepIndexContext,
  SetCurrentStepIndexContext,
  StepperDataContext
} from '../../../Contexts/StepperContext';
import { AlertsDispatchContext } from '../../../Contexts/AlertsContext';
import { ALERT_TYPE } from '../../../Components/Alerts/alert_enums';
import { useContext } from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { history } from '../../../Routes/history';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import useScreenTimeout from '../../../Hooks/useScreenTimeout';
import {
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core';
import Title from '../../../Components/Labels/Title';
import CurrencyInput from '../../../Components/Inputs/CurrencyInput';
import { useForm } from 'react-hook-form';
import {
  CalculateRechargeAPI,
  GeneratePrepaidMeterCouponAPI
} from '../../../API/PrepaidMeters/PrepaidMetersAPI';
import { defaultAlertError } from '../../../Components/Alerts/AlertMessages';
import { TYPE_OF_PAYMENTS } from '../../Payment/payment_enums';
import SweetAlert from '../../../Components/Alerts/SweetAlert';
import ButtonWithIcon from '../../../Components/Buttons/ButtonWithIcon';
import BackIcon from '../../../Components/CustomIcons/BackIcon';
import { MAX_RECHARGE_VALUE } from '../PrepaidMeter_enums';
import { config } from '../../../Configs';

const SEVEN_MINUTES_SECONDS = 7 * 60;
const TWO_MINUTES_SECONDS = 2 * 60;

const TOTAL_TIMEOUT =
  parseInt(config.prepaidMeter.timeout, 10) || SEVEN_MINUTES_SECONDS;

const NOTIFICATION_DELAY =
  parseInt(config.prepaidMeter.notificationDelay, 10) || TWO_MINUTES_SECONDS;

const RechargeCalculationStep = props => {
  const {
    formId,
    setCurrentStep: setVisualStepperIndex,
    setNextButtonText,
    setGoBack,
    setCanSubmit,
    requestId,
    setRequestId,
    authToken,
    redirectWithCoupon
  } = props;

  const classes = useStyles();

  const setCurrentStep = useContext(SetCurrentStepIndexContext);
  const currentStepIndex = useContext(CurrentStepIndexContext);
  const stepperData = useContext(StepperDataContext);
  const [loading, setLoading] = useState(false);
  const [rechargeData, setRechargeData] = useState(null);
  const [money, setMoney] = useState();
  const setAlert = useContext(AlertsDispatchContext);

  const handleTimeout = useCallback(
    () => history.replace(ROUTE_NAMES.home),
    []
  );

  const {
    showBanner: showTimeoutBanner,
    formattedCountdown,
    clear: clearScreenTimeout
  } = useScreenTimeout({
    totalSeconds: TOTAL_TIMEOUT,
    notificationDelaySeconds: NOTIFICATION_DELAY,
    onTimeout: handleTimeout
  });

  const onBackward = useCallback(() => {
    clearScreenTimeout();
    setCurrentStep(step => step - 1);
  }, [setCurrentStep, clearScreenTimeout]);

  useEffect(() => {
    setNextButtonText('Ir a pagar');
    setVisualStepperIndex(currentStepIndex);
    setGoBack({ action: onBackward });
    setCanSubmit(false);
  }, [
    setNextButtonText,
    setVisualStepperIndex,
    currentStepIndex,
    setGoBack,
    onBackward,
    setCanSubmit
  ]);

  const generateCoupon = useCallback(async () => {
    const { success, error, data } = await GeneratePrepaidMeterCouponAPI(
      authToken,
      requestId,
      {
        url: window.location.pathname
      }
    );

    if (!success) {
      defaultAlertError(error, setAlert);
      return;
    }
    const { couponId } = data;
    return couponId;
  }, [authToken, setAlert, requestId]);

  const handleRechargeCalculation = useCallback(async () => {
    if (!money) {
      setAlert({
        type: ALERT_TYPE.ERROR,
        message: 'Por favor ingresa un valor para tu recarga.'
      });
      return;
    }
    setLoading(true);

    const { success, data, error } = await CalculateRechargeAPI(
      authToken,
      money,
      stepperData.prepaidMeterCard.meterSerial
    );

    if (!success) {
      defaultAlertError(error, setAlert);

      setCanSubmit(true);
      setLoading(false);
      return;
    }

    const {
      paymentValue,
      rawUnits,
      contractId,
      requestId: generatedRequestId
    } = data.data;
    setRequestId(generatedRequestId);
    setRechargeData({
      meter: stepperData.prepaidMeterCard.meterSerial,
      m3: rawUnits,
      total: paymentValue,
      contractId
    });

    setCanSubmit(true);
    setLoading(false);
  }, [
    setLoading,
    setRechargeData,
    setCanSubmit,
    stepperData,
    setRequestId,
    authToken,
    setAlert,
    money
  ]);

  const { handleSubmit } = useForm({
    submitFocusError: true,
    defaultValues: {
      ...stepperData
    }
  });

  const goToPayment = useCallback(async () => {
    if (!rechargeData) {
      return;
    }

    setLoading(true);
    setCanSubmit(false);
    const couponId = await generateCoupon();
    setLoading(false);
    setCanSubmit(true);
    if (!couponId) {
      return;
    }

    clearScreenTimeout();

    redirectWithCoupon(
      {
        type: TYPE_OF_PAYMENTS.coupon,
        couponId,
        detail: {
          isPaid: false,
          isPending: false,
          couponId,
          contractId: rechargeData.contractId,
          value: rechargeData.total
        },
        canBePaidPartially: false
      },
      rechargeData.meter,
      rechargeData.m3
    );
  }, [
    rechargeData,
    generateCoupon,
    redirectWithCoupon,
    setLoading,
    setCanSubmit,
    clearScreenTimeout
  ]);

  return (
    <>
      <div className={classes.root}>
        <div className={classes.contentInner}>
          {showTimeoutBanner && (
            <SweetAlert
              id="prepaid_meter_timeout_banner"
              type={ALERT_TYPE.WARNING}
              classes={{
                root: classes.timeoutBanner,
                message: classes.timeoutBannerText
              }}
              message={
                <Fragment>
                  Tienes <strong>{formattedCountdown}</strong> para completar tu
                  recarga o serás redireccionado al inicio
                </Fragment>
              }
            />
          )}
          <Grid container className={classes.rechargeHeader}>
            <Typography className={classes.headerTitle}>
              N° de medidor
            </Typography>
            <Chip
              className={classes.serialChip}
              label={stepperData.prepaidMeterCard.meterSerial}
            />
          </Grid>
          <Title title="¿Cuánto vas a recargar?" className={classes.title} />
          <form id={formId} onSubmit={handleSubmit(goToPayment)}></form>
          <form
            id="recharge_calculation_form"
            className={classes.form}
            onSubmit={handleSubmit(handleRechargeCalculation)}
          >
            <div className={classes.valueFieldContainer}>
              <CurrencyInput
                fullWidth
                name="moneyToCalculate"
                thousandSeparator="."
                decimalSeparator=","
                disabled={loading}
                value={money}
                onValueChange={({ floatValue }) => setMoney(floatValue)}
                allowNegative={false}
                decimalScale={0}
                placeholder="$  Ingresa el valor aquí"
                prefix="$  "
                type="tel"
                inputMode="numeric"
                isAllowed={({ floatValue }) => {
                  return (
                    floatValue === undefined || floatValue < MAX_RECHARGE_VALUE
                  );
                }}
              />
              <Grid
                container
                justifyContent="flex-end"
                className={classes.buttonContainer}
              >
                <ButtonWithIcon
                  className={classes.details}
                  buttonText={'Calcular'}
                  disabled={loading && money}
                  type="submit"
                >
                  <BackIcon className={classes.detailsIcon} size={16} />
                </ButtonWithIcon>
              </Grid>
              {loading && (
                <CircularProgress className={classes.loadingAnimation} />
              )}
            </div>
          </form>
          {rechargeData && (
            <>
              <SweetAlert
                id="gas_units_equivalence_sweetalert"
                message={
                  <Fragment>
                    Equivale a {rechargeData.m3 || 0} metros cúbicos en tu
                    medidor
                  </Fragment>
                }
              />
              <Grid container className={classes.rechargeInfoContainer}>
                <Typography className={classes.infoTitle}>
                  Valor a recargar
                </Typography>
                <Typography className={classes.infoPrice}>
                  {(rechargeData.total || 0).toLocaleString('es-CO', {
                    style: 'currency',
                    currency: 'COP',
                    minimumFractionDigits: 0
                  })}
                </Typography>
                <Typography className={classes.infoText}>
                  Ajustamos el valor para coincidir con el volumen exacto de
                  gas.
                </Typography>
              </Grid>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '14px 10px 16px'
  },
  contentInner: {
    width: '100%',
    maxWidth: 700,
    margin: '0 auto'
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    textAlign: 'center',
    margin: '19px 0 10px'
  },
  form: {
    marginBottom: theme.spacing(3),
    width: '100%'
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: 500
  },
  infoPrice: {
    fontSize: 15,
    fontWeight: 600
  },
  infoText: {
    fontSize: theme.typography.body1.fontSize,
    textAlign: 'justify',
    width: '100%'
  },
  rechargeHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.background.dark,
    padding: theme.spacing(2),
    borderRadius: theme.custom.borderRadius
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: theme.typography.fontWeightBold
  },
  serialChip: {
    fontSize: 12,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    borderRadius: '10px',
    height: '26px'
  },
  rechargeInfoContainer: {
    marginTop: '19px',
    backgroundColor: theme.palette.background.dark,
    border: 'solid 1px',
    borderColor: theme.palette.background.border,
    borderRadius: theme.custom.borderRadius,
    padding: theme.spacing(2),
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  valueFieldContainer: {
    position: 'relative'
  },
  loadingAnimation: {
    position: 'absolute',
    width: '20px !important',
    height: '20px !important',
    right: '16px',
    top: '36px'
  },
  details: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&[disabled]': {
      opacity: '0.5'
    }
  },
  detailsIcon: {
    transform: 'rotate(180deg)',
    fill: theme.palette.primary.main
  },
  buttonContainer: {
    padding: theme.spacing(2)
  },
  timeoutBanner: {
    marginBottom: theme.spacing(2),
    opacity: 1,
    width: '100%'
  },
  timeoutBannerText: {
    fontWeight: 600,
    fontSize: 14
  }
}));

export default RechargeCalculationStep;
